import { useMutation } from "@tanstack/react-query";
import { useState } from "react";

import { get, post } from "@/api";
import { removeNonDigits } from "@/lib/utils";
import type { GiftCardFormData } from "@/modules/giftCards/GiftCardPurchaseForm/GiftCardPurchaseForm";
import { queryClient } from "@/pages/Root";
import Services from "@/services/Services";

interface UseGiftCardPhoneVerificationProps {
  isAuthenticated: boolean;
  onSuccess: (data: GiftCardFormData) => Promise<void>;
  onFormDataUpdate?: (data: GiftCardFormData) => void;
  onShowModal: () => void;
}

interface UseGiftCardPhoneVerificationReturn {
  // State
  showPhoneVerification: boolean;
  verificationCode: string;
  verificationError: boolean;
  isVerifying: boolean;
  smsSendError: boolean;
  isSendingSms: boolean;
  pendingFormData: GiftCardFormData | null;

  // Actions
  handleFormSubmit: (data: GiftCardFormData) => Promise<void>;
  handlePhoneVerification: (code: string) => Promise<void>;
  handleResendCode: () => Promise<void>;
  setVerificationCode: (code: string) => void;
  resetVerification: () => void;
}

export const useGiftCardPhoneVerification = ({
  isAuthenticated,
  onSuccess,
  onFormDataUpdate,
  onShowModal,
}: UseGiftCardPhoneVerificationProps): UseGiftCardPhoneVerificationReturn => {
  // State management
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [smsSendError, setSmsSendError] = useState(false);
  const [isSendingSms, setIsSendingSms] = useState(false);
  const [pendingFormData, setPendingFormData] =
    useState<GiftCardFormData | null>(null);

  // Mutations
  const sendSmsMutation = useMutation({
    mutationFn: (phoneNumber: string) => post("/auth/login", { phoneNumber }),
  });

  const verifyCodeMutation = useMutation({
    mutationFn: async ({
      phoneNumber,
      verificationCode,
      userFirstName,
      userLastName,
      userEmail,
    }: {
      phoneNumber: string;
      verificationCode: string;
      userFirstName: string;
      userLastName: string;
      userEmail?: string;
    }) => {
      const verify = await post("/auth/verify", {
        phoneNumber,
        verificationCode,
        userFirstName,
        userLastName,
        userEmail,
      });

      if (!verify.token) {
        throw new Error("Failed to verify");
      }

      // Set the auth token and fetch user data
      await Services.auth.setToken(verify.token);
      await queryClient.fetchQuery(["/user"], () => get("/user"));
      await queryClient.invalidateQueries({ queryKey: ["/user"] });

      return verify;
    },
  });

  // Handlers
  const handlePhoneVerification = async (code: string) => {
    if (!pendingFormData) return;

    setVerificationError(false);
    setIsVerifying(true);

    try {
      const phoneNumber = `+1${removeNonDigits(
        pendingFormData.senderPhoneNumber || ""
      )}`;
      await verifyCodeMutation.mutateAsync({
        phoneNumber,
        verificationCode: code,
        userFirstName: pendingFormData.senderFirstName,
        userLastName: pendingFormData.senderLastName,
        userEmail: pendingFormData.senderEmail,
      });

      await onSuccess(pendingFormData);

      // Update form data with authenticated user info
      if (onFormDataUpdate) {
        onFormDataUpdate(pendingFormData);
      }

      setShowPhoneVerification(false);
      setPendingFormData(null);
      setVerificationCode("");
      setVerificationError(false); // Ensure error is cleared on success
      onShowModal();
    } catch (error) {
      setVerificationError(true);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    if (!pendingFormData) return;

    setIsSendingSms(true);
    try {
      const phoneNumber = `+1${removeNonDigits(
        pendingFormData.senderPhoneNumber || ""
      )}`;
      await sendSmsMutation.mutateAsync(phoneNumber);
      setSmsSendError(false);
    } catch (error) {
      console.error("Failed to resend code:", error);
      setSmsSendError(true);
    } finally {
      setIsSendingSms(false);
    }
  };

  const handleFormSubmit = async (data: GiftCardFormData) => {
    if (!isAuthenticated) {
      setPendingFormData(data);
      setIsSendingSms(true);
      setSmsSendError(false);

      try {
        const phoneNumber = `+1${removeNonDigits(
          data.senderPhoneNumber || ""
        )}`;
        await sendSmsMutation.mutateAsync(phoneNumber);
        setSmsSendError(false);
        // Only show phone verification after successful SMS send
        setShowPhoneVerification(true);
      } catch (error) {
        console.error("Failed to send SMS:", error);
        setSmsSendError(true);
        // Show phone verification even on error to display error state
        setShowPhoneVerification(true);
      } finally {
        setIsSendingSms(false);
      }
      return;
    }

    await onSuccess(data);
  };

  const resetVerification = () => {
    setShowPhoneVerification(false);
    setPendingFormData(null);
    setVerificationCode("");
    setVerificationError(false);
    setSmsSendError(false);
    setIsSendingSms(false);
  };

  return {
    // State
    showPhoneVerification,
    verificationCode,
    verificationError,
    isVerifying,
    smsSendError,
    isSendingSms,
    pendingFormData,

    // Actions
    handleFormSubmit,
    handlePhoneVerification,
    handleResendCode,
    setVerificationCode,
    resetVerification,
  };
};
